import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import exportIcon from "../../../assets/icons/upload.svg";
import ColumnCustomizer from "../../../components/ColumnCustomizer";
import ExportModal from "../../../components/ImpExpModal/ExportModal";
import Loader from "../../../components/Loader";
import ContactPagination from "../../../components/Pagination/ContactPagination";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const MarketMetrics = ({
    isSidebarCollapsed,
    onCallApi,
    exportCsvData,
    selectedData,
    queryParams,
    filterData,
    from
}) => {
    const [config] = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [exportType, setExportType] = useState("DealMetrics");
    const [loading, setLoading] = useState(true);

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemPerPage, setItemPerPage] = useState(10);

    const [showColumnModal, setShowColumnModal] = useState(false);
    const [paginationData, setPaginationData] = useState({
        totalItems: 0,
        from: 0,
        to: 0,
        totalPages: 0,
    });

    // ==================== COLUMN CUSTOMIZATION ====================
    const allColumns = [
        { key: 'property_name', label: 'Property Name', fixed: true },
        { key: 'lease_type', label: 'Lease Type' },
        { key: 'lease_term', label: 'Lease Term Remaining' },
        { key: 'noi', label: 'NOI' },
        { key: 'contract_price', label: 'Contract Price' },
        { key: 'cap_rate', label: 'Contract Cap Rate' },
        { key: 'asking_percent', label: '% of Asking Price' },
    ];

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem('marketMetricsVisibleColumns');
        return saved
            ? JSON.parse(saved)
            : allColumns.map(col => col.key);
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    const formatPercent = (value, decimals = 2) => {
        return value !== null && value !== undefined
            ? Number(value).toFixed(decimals) + "%"
            : "N/A";
    };

    const formatYears = (years) => {
        return years ? Number(years).toFixed(1) + " yrs" : "N/A";
    };

    const fetchDashboardData = () => {
        setLoading(true);
        
        const fullQuery = `${queryParams}&page=${currentPage}&per_page=${itemPerPage}`;

        axios
            .get(`${BASE_URL}/market-metrics?${fullQuery}`, config)
            .then((res) => {
                if (res.data?.success) {
                    const data = res.data.data;
                    setDashboardData(data);

                    // Handle both possible structures
                    const dealMetrics = data?.deal_metrics || data;

                    setPaginationData({
                        totalItems: dealMetrics?.total ?? 0,
                        from: dealMetrics?.from ?? 0,
                        to: dealMetrics?.to ?? 0,
                        totalPages: dealMetrics?.last_page ?? 0,
                    });
                }
            })
            .catch((err) => {
                if (err.response?.data?.message &&
                    err.response.data.message !== "Too Many Attempts.") {
                    NotificationManager.error(err.response.data.message);
                }
            })
            .finally(() => setLoading(false));
    };

    const handleNext = () => {
        if (currentPage < paginationData.totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleItemPerPageSelect = (value) => {
        setItemPerPage(value);
        setCurrentPage(1);
    };

    // Fetch data when pagination or filters change
    useEffect(() => {
        fetchDashboardData();
    }, [currentPage, itemPerPage, queryParams]);

    if (loading) return <Loader />;
    if (!dashboardData) return <div className="p-8 text-center">Failed to load market metrics</div>;

    const mm = dashboardData.market_metrics || {};
    const deals = dashboardData.deal_metrics?.data || [];

    // Lease Type Mix
    const leaseTypeMix = [
        { type: "NNN", percent: mm.lease_type_mix?.nnn || 0, color: "#15803d" },
        { type: "Gross", percent: mm.lease_type_mix?.gross || 0, color: "#4ade80" },
        { type: "Modified", percent: mm.lease_type_mix?.modified || 0, color: "#86efac" },
    ].filter(item => item.percent > 0);

    const dominantPercent = leaseTypeMix[0]?.percent || 0;

    const handleExport = (type) => {
        setExportType(type);
        setShowModal(true);
    };

    return (
        <div className="light-bg-L pb-3 px-4 py-4">
            <div className="light-bg-L">

                {/* Top Metrics Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 mb-6 items-stretch">

                    <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg Contract Price</p>
                            <p className="head-2 dark-H mt-1 text-[28px]">
                                {formatCurrency(mm.avg_contract_price)}
                            </p>
                            <div className="mt-1 space-y-0.5">
                                <p className="body-S text-gray-500">Total: {formatCurrency(mm.total_contract_price)}</p>
                                <p className="body-S text-gray-500">Deals: {mm.deal_count}</p>
                            </div>
                        </div>

                        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg Contract Cap Rate</p>
                            <p className="head-2 dark-H mt-1 text-[28px]">
                                {formatPercent(mm.contract_cap_rate?.avg)}
                            </p>
                            <div className="mt-1 space-y-0.5">
                                <p className="body-S text-gray-500">
                                    Range: {formatPercent(mm.contract_cap_rate?.min)} – {formatPercent(mm.contract_cap_rate?.max)}
                                </p>
                                <p className="body-S text-gray-500">Deals: {mm.contract_cap_rate?.deal_count || mm.deal_count}</p>
                            </div>
                        </div>

                        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg % of Asking Price</p>
                            <p className="head-2 dark-H mt-1 text-[28px]">
                                {formatPercent(mm.asking_price_percentage?.avg, 1)}
                            </p>
                            <div className="mt-1 space-y-0.5">
                                <p className="body-S text-gray-500">
                                    Range: {mm.asking_price_percentage?.min}% – {mm.asking_price_percentage?.max}%
                                </p>
                                <p className="body-S text-gray-500">Deals: {mm.asking_price_percentage?.deal_count || mm.deal_count}</p>
                            </div>
                        </div>

                        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg Lease Term Remaining</p>
                            <p className="head-2 dark-H mt-1 text-[28px]">
                                {formatYears(mm.lease_term_remaining?.avg)}
                            </p>
                            <div className="mt-1 space-y-0.5">
                                <p className="body-S text-gray-500">
                                    Range: {formatYears(mm.lease_term_remaining?.min)} – {formatYears(mm.lease_term_remaining?.max)}
                                </p>
                                <p className="body-S text-gray-500">Deals: {mm.lease_term_remaining?.deal_count || mm.deal_count}</p>
                            </div>
                        </div>
                    </div>

                    {/* Lease Type Mix */}
                    <div className="lg:col-span-3 bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                        <p className="body-N text-gray-600 mb-5">Lease Type Mix</p>
                        <div className="flex items-center justify-around gap-8">
                            <div className="flex-shrink-0">
                                <div className="relative w-28 h-28">
                                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-12">
                                        <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                                        <circle
                                            cx="50" cy="50" r="45"
                                            fill="none"
                                            stroke="#15803d"
                                            strokeWidth="10"
                                            strokeDasharray={`${dominantPercent * 2.83} ${100 * 2.83}`}
                                            strokeDashoffset="25"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <div className="space-y-2 pt-1">
                                {leaseTypeMix.map((item, i) => (
                                    <div key={i} className="flex items-center gap-8">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                                            <span className="head-5 capitalize">{item.type}</span>
                                        </div>
                                        <span className="dark-M body-N">{item.percent}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <p className="body-S text-gray-500 mt-auto pt-6 text-center text-xs">
                            Total Deals: {mm.deal_count}
                        </p>
                    </div>
                </div>

                {/* Deal Metrics Table */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <p className="head-4 dark-H">
                            Deal Metrics <span className="text-gray-500">({mm.deal_count} deals)</span>
                        </p>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowColumnModal(true)}
                                className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
                            >
                                Columns ⚙️
                            </button>
                            <div className="flex items-center cursor-pointer" onClick={() => handleExport("DealMetrics")}>
                                <img src={exportIcon} alt="" className="mr-2" />
                                <p className="text-green-H body-S">Export</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border overflow-hidden">
                        <div className="max-h-[380px] overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            <table className="w-full min-w-full">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr className="uppercase body-N dark-M text-left bg-[#d1dbcf]">
                                        {visibleColumns.includes('property_name') && <th className="p-4">Property Name</th>}
                                        {visibleColumns.includes('lease_type') && <th className="p-4 text-center">Lease Type</th>}
                                        {visibleColumns.includes('lease_term') && <th className="p-4 text-center">Lease Term Remaining</th>}
                                        {visibleColumns.includes('noi') && <th className="p-4 text-center">NOI</th>}
                                        {visibleColumns.includes('contract_price') && <th className="p-4 text-center">Contract Price</th>}
                                        {visibleColumns.includes('cap_rate') && <th className="p-4 text-center">Contract Cap Rate</th>}
                                        {visibleColumns.includes('asking_percent') && <th className="p-4 text-center">% of Asking Price</th>}
                                    </tr>
                                </thead>

                                <tbody className="body-N dark-M divide-y">
                                    {deals.map((deal) => (
                                        <tr key={deal.id} className="hover:bg-gray-50">
                                            {visibleColumns.includes('property_name') && (
                                                <td className="p-4">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{deal.property_name}</p>
                                                        <p className="text-gray-500 text-sm">{deal.street_address}</p>
                                                        <p className="text-gray-500 text-sm">
                                                            {deal.city}, {deal.state}
                                                        </p>
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.includes('lease_type') && (
                                                <td className="p-4 text-center">{deal.lease_type}</td>
                                            )}
                                            {visibleColumns.includes('lease_term') && (
                                                <td className="p-4 text-center">{formatYears(deal.lease_term_remaining)}</td>
                                            )}
                                            {visibleColumns.includes('noi') && (
                                                <td className="p-4 text-center">{formatCurrency(deal.noi)}</td>
                                            )}
                                            {visibleColumns.includes('contract_price') && (
                                                <td className="p-4 text-center">{formatCurrency(deal.contract_price)}</td>
                                            )}
                                            {visibleColumns.includes('cap_rate') && (
                                                <td className="p-4 text-center">{formatPercent(deal.contract_cap_rate)}</td>
                                            )}
                                            {visibleColumns.includes('asking_percent') && (
                                                <td className="p-4 text-center">{formatPercent(deal.asking_price_percentage, 1)}</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <ContactPagination
                            from="marketmetrics"
                            paginationData={paginationData}
                            handleNext={handleNext}
                            handlePrev={handlePrev}
                            itemPerPage={itemPerPage}
                            handleItemPerPageSelect={handleItemPerPageSelect}
                        />

                        {deals.length === 0 && (
                            <div className="p-8 text-center text-gray-500">No deals found</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Column Customizer Modal */}
            {showColumnModal && (
                <ColumnCustomizer
                    allColumns={allColumns}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    storageKey="marketMetricsVisibleColumns"
                    onClose={() => setShowColumnModal(false)}
                />
            )}

            {/* Export Modal */}
            {showModal && (
                <ExportModal
                    showModal={showModal}
                    selectedData={selectedData}
                    exportCsvData={exportCsvData}
                    from={from}
                    type={exportType}
                    onCallApi={onCallApi}
                    filterData={filterData}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default MarketMetrics;